import { apiGet, apiPost } from '../api';
import type { Preferences } from '../types';
import { esc } from '../util';

/** Render the settings page. */
export function renderSettings(container: HTMLElement): void {
  container.innerHTML = `
    <div class="settings-page">
      <h2 class="page-title">Settings</h2>
      <div id="settings-form" class="settings-form">
        <div class="skeleton-card"><div class="skeleton-line"></div><div class="skeleton-line"></div><div class="skeleton-line"></div></div>
        <div class="skeleton-card"><div class="skeleton-line"></div><div class="skeleton-line"></div></div>
      </div>
    </div>
  `;

  loadSettings(container);
}

async function loadSettings(container: HTMLElement): Promise<void> {
  const form = container.querySelector<HTMLElement>('#settings-form');
  if (!form) return;

  try {
    const prefs = await apiGet<Preferences>('/api/settings');
    form.innerHTML = `
      <div class="settings-section-label">Display</div>
      <div class="setting-row">
        <label class="setting-label">UI Mode</label>
        <select id="pref-ui-mode" class="input select">
          <option value="deck" ${prefs.ui_mode === 'deck' ? 'selected' : ''}>Deck</option>
          <option value="dashboard" ${prefs.ui_mode === 'dashboard' ? 'selected' : ''}>Dashboard</option>
        </select>
      </div>

      <div class="setting-row">
        <label class="setting-label">Theme</label>
        <select id="pref-theme" class="input select">
          <option value="dark" ${prefs.theme === 'dark' ? 'selected' : ''}>Dark</option>
          <option value="light" ${prefs.theme === 'light' ? 'selected' : ''}>Light</option>
        </select>
      </div>

      <div class="settings-section-label">Connection</div>
      <div class="setting-row">
        <label class="setting-label">Auto Connect</label>
        <select id="pref-auto-connect" class="input select">
          <option value="true" ${prefs.auto_connect === 'true' ? 'selected' : ''}>Yes</option>
          <option value="false" ${prefs.auto_connect === 'false' ? 'selected' : ''}>No</option>
        </select>
      </div>

      <div class="setting-row">
        <label class="setting-label">Connect Timeout (ms)</label>
        <input type="number" id="pref-timeout" class="input" value="${esc(prefs.connect_timeout_ms)}" />
      </div>

      <button id="btn-save-settings" class="btn-primary">Save</button>
      <p id="settings-status" class="status-text hidden"></p>
    `;

    const btnSave = form.querySelector<HTMLButtonElement>('#btn-save-settings');
    btnSave?.addEventListener('click', async () => {
      const pairs: Array<{ key: string; value: string }> = [
        { key: 'ui_mode', value: (form.querySelector<HTMLSelectElement>('#pref-ui-mode'))?.value ?? 'deck' },
        { key: 'theme', value: (form.querySelector<HTMLSelectElement>('#pref-theme'))?.value ?? 'dark' },
        { key: 'auto_connect', value: (form.querySelector<HTMLSelectElement>('#pref-auto-connect'))?.value ?? 'true' },
        { key: 'connect_timeout_ms', value: (form.querySelector<HTMLInputElement>('#pref-timeout'))?.value ?? '2000' },
      ];

      for (const pair of pairs) {
        await apiPost('/api/settings', pair);
      }

      const status = form.querySelector<HTMLElement>('#settings-status');
      if (status) {
        status.textContent = 'Settings saved';
        status.classList.remove('hidden');
        setTimeout(() => status.classList.add('hidden'), 2000);
      }

      // Apply theme immediately
      const theme = (form.querySelector<HTMLSelectElement>('#pref-theme'))?.value ?? 'dark';
      document.documentElement.setAttribute('data-theme', theme);
    });
  } catch {
    form.innerHTML = '<p class="empty-text">Could not load settings</p>';
  }
}

